import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { authService } from '../../services/authService';
import { CheckCircle, AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

const VerifyEmail = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(60);

  useEffect(() => {
    // Auto-verify email on component mount
    if (token) {
      verifyEmail();
    } else {
      setError('Invalid verification token');
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Countdown timer and auto-redirect
    if (isSuccess && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (isSuccess && countdown === 0) {
      navigate('/login');
    }
  }, [isSuccess, countdown, navigate]);

  const verifyEmail = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await authService.verifyEmail(token);
      setIsSuccess(true);
      toast.success('Email verified successfully!');
    } catch (err) {
      console.error('Error verifying email:', err);
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          'Failed to verify email. The token may be invalid or expired.';
      setError(errorMessage);
      
      
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-primary-600 rounded-full flex items-center justify-center mb-4">
              <Loader2 className="h-8 w-8 text-white animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Verifying your email
            </h2>
            <p className="text-gray-600">
              Please wait while we verify your email address...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    // Check if the error is about email already being verified
    const isAlreadyVerified = error?.toLowerCase().includes('already verified') || 
                              error?.toLowerCase().includes('email is already verified');

    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            {isAlreadyVerified ? (
              <>
                <div className="mx-auto h-16 w-16 bg-success-600 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {error}
                </h2>
              </>
            ) : (
              <>
                <div className="mx-auto h-16 w-16 bg-red-600 rounded-full flex items-center justify-center mb-4">
                  <AlertCircle className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Verification Failed
                </h2>
                <p className="text-gray-600 mb-6">
                  {error}
                </p>
              </>
            )}
            
            <div className="space-y-4">
              <Link
                to="/login"
                className="btn-primary inline-flex items-center"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go to Login
              </Link>
            </div>
          </div>

          <div className="text-center text-sm text-gray-500">
            <p>&copy; 2024 WABot. All rights reserved.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-success-600 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Email Verified Successfully!
            </h2>
            <p className="text-gray-600 mb-2">
              Your email address has been verified. You can now log in to your account.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Redirecting to login page in {countdown} seconds...
            </p>
            
            <div className="space-y-4">
              <button
                onClick={handleLoginClick}
                className="btn-primary w-full inline-flex items-center justify-center"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go to Login
              </button>
            </div>
          </div>

          <div className="text-center text-sm text-gray-500">
            <p>&copy; 2024 WABot. All rights reserved.</p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default VerifyEmail;

